import { useEffect, memo, CSSProperties } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectValidParams, selectGridData, selectShowReasonColumn } from '@pages/reporting-pages/reduced-orders/store/selectors';
import { createGridData } from '@pages/reporting-pages/reduced-orders/store/slice';
import { useLazyReducedOrdersQuery } from '@api/order-reporting/api';
import { GridLoading, GridError, GridEmpty, GridNoParams } from 'components/global/GridElements';
import { useGridSorting } from 'components/filtering/filter-logic/useGridSorting';
import { useGridFiltering } from 'components/filtering/filter-logic/useGridFiltering';
import GridHeader from '@pages/reporting-pages/reduced-orders/grid/GridHeader';
import GridSummary from '@pages/reporting-pages/reduced-orders/grid/GridSummary';
import GridRow from '@pages/reporting-pages/reduced-orders/grid/GridRow';
import { ReducedOrdersReport } from '@api/order-reporting/types/ReducedOrdersReport';
import { getHeadersObject } from '@pages/reporting-pages/reduced-orders/headersObject';



const Grid = () => {

	const validParams = useSelector(selectValidParams);
	const gridData = useSelector(selectGridData);
	const showReasonColumn = useSelector(selectShowReasonColumn);
	const [ runQuery, { data, isLoading, isFetching, isError, isSuccess } ] = useLazyReducedOrdersQuery();
	const [ filteredData, filterObject, handleSearchValues, handleAddFilterValues, clearFilter ] = useGridFiltering(gridData);
	const [ sortedData, sortObject, handleSorting, clearSorting ] = useGridSorting(filteredData);

	const dispatch = useDispatch();
	const runCreateGridData = (data: ReducedOrdersReport[] | undefined | null) => dispatch(createGridData(data));

	const className = 'app-grid reduced-orders-grid';
	const style: CSSProperties = {
		gridTemplateColumns: getHeadersObject(showReasonColumn).map(x => x.width).join(' '),
	}


	useEffect(() => {
		if (validParams) {
			runQuery({ ...validParams }, true);
		}
	}, [ validParams ]);


	useEffect(() => {
		if (data) {
			runCreateGridData(data);
		}
	}, [ data ]);


	if (!validParams) return (
		<div className={className} style={style}>
			<GridHeader showReasonColumn={showReasonColumn} />
			<GridNoParams headersLength={getHeadersObject(showReasonColumn).length} />
		</div>
	);

	if (isLoading || isFetching) return (
		<div className={className} style={style}>
			<GridHeader showReasonColumn={showReasonColumn} />
			<GridLoading headersLength={getHeadersObject(showReasonColumn).length} />
		</div>
	);

	if (isError || !data) return (
		<div className={className} style={style}>
			<GridHeader showReasonColumn={showReasonColumn} />
			<GridError headersLength={getHeadersObject(showReasonColumn).length} />
		</div>
	);
	
	if (isSuccess && sortedData && sortedData.length === 0) return (
		<div className={className} style={style}>
			<GridHeader
				showReasonColumn={showReasonColumn}
				filterObject={filterObject}
				sortObject={sortObject}
				onSearchValues={handleSearchValues}
				onAddFilterValues={handleAddFilterValues}
				onClearFilter={clearFilter}
				onSorting={handleSorting}
				onClearSorting={clearSorting}
			/>
			<GridEmpty headersLength={getHeadersObject(showReasonColumn).length} />
		</div>
	);

	return (
		<div className={className} style={style}>
			
			<GridHeader
				showReasonColumn={showReasonColumn}
				filterObject={filterObject}
				sortObject={sortObject}
				onSearchValues={handleSearchValues}
				onAddFilterValues={handleAddFilterValues}
				onClearFilter={clearFilter}
				onSorting={handleSorting}
				onClearSorting={clearSorting}
			/>

			<GridSummary
				data={sortedData}
				showReasonColumn={showReasonColumn}
			/>
				
			{sortedData && sortedData.map((x,i) =>
				<GridRow
					key={`id-reduced-orders-grid-${x.orderUnversionedSystemId ? x.orderUnversionedSystemId : 'without-system-id'}`}
					item={x}
					rowIndex={i}
					validParams={validParams}
					showReasonColumn={showReasonColumn}
				/>
			)}
		</div>
	)
}

export default memo(Grid);